import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../constants/api';
import ContactLogo from '../components/shared/ContactLogo';

// The installed lucide-react version doesn't ship brand/logo icons, so these
// three are simple hand-drawn stand-ins in the same 24px stroke style as
// the rest of the lucide set used across the app.
const InstagramIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const LinkedinIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4V8h4v2a5.5 5.5 0 014-2z" />
    <rect x="2" y="9" width="4" height="12" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

const GithubIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 19c-4.3 1.4-4.3-2.5-6-3m12 5v-3.5c0-1 .1-1.4-.5-2 2.8-.3 5.5-1.4 5.5-6a4.6 4.6 0 00-1.3-3.2 4.2 4.2 0 00-.1-3.2s-1.1-.3-3.5 1.3a12.3 12.3 0 00-6.2 0C6.5 2.8 5.4 3.1 5.4 3.1a4.2 4.2 0 00-.1 3.2A4.6 4.6 0 004 9.5c0 4.6 2.7 5.7 5.5 6-.6.6-.6 1.2-.5 2V21" />
  </svg>
);

const NUM_BARS = 15;

// Tallest at the edges, shortest at the center — same curve as the source
// component, just re-derived here instead of relying on a Tailwind config
// this project doesn't have.
const barHeight = (index, total) => {
  const position = index / (total - 1);
  const distanceFromCenter = Math.abs(position - 0.5);
  const heightPercentage = Math.pow(distanceFromCenter * 2, 1.2);
  return 30 + (100 - 30) * heightPercentage;
};

const GradientBars = () => (
  <div className="wl-bars" aria-hidden="true">
    {Array.from({ length: NUM_BARS }).map((_, index) => (
      <div
        key={index}
        className="wl-bar"
        style={{
          transform: `scaleY(${barHeight(index, NUM_BARS) / 100})`,
          animationDelay: `${index * 0.1}s`,
        }}
      />
    ))}
  </div>
);

const WaitlistForm = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;
    setIsSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/waitlist/subscribe`, { email });
      setIsSubmitted(true);
      setEmail('');
      setTimeout(() => setIsSubmitted(false), 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo completar el registro. Intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="wl-form-wrap" id="waitlist-form">
      {!isSubmitted ? (
        <>
          <form onSubmit={handleSubmit} className="wl-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Ingresa tu correo"
              className="wl-input"
              required
            />
            <button type="submit" disabled={isSubmitting} className="wl-btn-white wl-form-btn">
              {isSubmitting ? <span className="wl-spinner" /> : 'Unirme a la lista de espera'}
            </button>
          </form>
          {error && <p className="wl-error">{error}</p>}
        </>
      ) : (
        <div className="wl-success">¡Gracias! Te avisaremos cuando lancemos.</div>
      )}
    </div>
  );
};

const WaitlistPage = () => {
  // Instrument Serif and Space Grotesk are only used on this page — loaded
  // here instead of globally in index.html so every other route doesn't
  // pay for fonts it never renders.
  useEffect(() => {
    const id = 'wl-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@1&family=Space+Grotesk:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
  }, []);

  return (
    <section className="wl-root">
      <GradientBars />

      <div className="wl-logo-invert wl-logo-fixed">
        <ContactLogo className="h-7 w-auto" clickable />
      </div>

      <div className="wl-content">
        <h1 className="wl-h1">
          <span className="wl-h1-line1">Redefiniendo lo que es posible,</span>
          <span className="wl-h1-line2">una experiencia a la vez.</span>
        </h1>

        <div className="wl-sub">
          <p>Sé el primero en saber cuándo lanzamos.</p>
          <p>Únete a la lista de espera y obtén acceso anticipado exclusivo.</p>
        </div>

        <div className="wl-form-container">
          <WaitlistForm />
        </div>

        <div className="wl-social">
          <a href="https://www.instagram.com/contact_cl" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><InstagramIcon /></a>
          <a href="https://www.linkedin.com/company/contactnetworkcl" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><LinkedinIcon /></a>
          <a href="#" aria-label="GitHub"><GithubIcon /></a>
        </div>
      </div>

      <style>{CSS}</style>
    </section>
  );
};

const CSS = `
.wl-root{position:relative;width:100%;min-height:100vh;display:flex;flex-direction:column;align-items:center;padding:0 24px;overflow-x:hidden;background:#030712;font-family:'Space Grotesk',system-ui,sans-serif;box-sizing:border-box}
.wl-root *{box-sizing:border-box}

/* GRADIENT BARS */
.wl-bars{position:absolute;inset:0;z-index:0;display:flex;overflow:hidden}
.wl-bar{flex:1 0 calc(100% / 15);max-width:calc(100% / 15);height:100%;background:linear-gradient(to top, rgb(0,102,255), transparent);transform-origin:bottom;transition:transform .5s ease-in-out;animation:wlPulseBar 2.4s ease-in-out infinite alternate}
@keyframes wlPulseBar{from{opacity:.75}to{opacity:1}}

.wl-btn-white{background:#fff;color:#0A0A0A;border:none;padding:10px 22px;border-radius:980px;font-family:inherit;font-size:14px;font-weight:600;cursor:pointer;transition:transform .2s,opacity .2s;white-space:nowrap}
.wl-btn-white:hover{transform:scale(1.04);opacity:.94}
.wl-btn-white:disabled{cursor:not-allowed;opacity:.6}

.wl-logo-fixed{position:fixed;top:24px;left:24px;z-index:50}

/* CONTENT */
.wl-content{position:relative;z-index:10;width:100%;min-width:0;max-width:860px;margin:0 auto;min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:64px 0;box-sizing:border-box}

.wl-h1{width:100%;min-width:0;max-width:100%;margin:0 0 28px;color:#fff;line-height:1.08;letter-spacing:-0.02em}
.wl-h1-line1{display:block;width:100%;font-family:'Inter',system-ui,sans-serif;font-weight:500;font-size:clamp(28px,6vw,60px)}
.wl-h1-line2{display:block;width:100%;font-family:'Instrument Serif',serif;font-style:italic;font-weight:400;font-size:clamp(28px,6vw,60px)}

.wl-sub{width:100%;min-width:0;max-width:100%;margin:0 0 40px}
.wl-sub p{margin:0;color:#9CA3AF;font-size:clamp(15px,2.4vw,20px);line-height:1.6}

.wl-form-container{width:100%;max-width:600px;margin:0 0 32px}
.wl-form-wrap{width:100%}
.wl-form{display:flex;flex-direction:column;gap:12px}
.wl-input{flex:1;padding:15px 26px;border-radius:980px;background:rgba(17,24,39,.6);border:1px solid rgba(255,255,255,.14);color:#fff;font-family:inherit;font-size:15px;outline:none;backdrop-filter:blur(6px);transition:border-color .2s}
.wl-input::placeholder{color:#9CA3AF}
.wl-input:focus{border-color:#fff}
.wl-form-btn{display:flex;align-items:center;justify-content:center;padding:15px 26px}
.wl-spinner{width:17px;height:17px;border:2px solid rgba(0,0,0,.25);border-top-color:#0A0A0A;border-radius:50%;animation:wlSpin .7s linear infinite}
@keyframes wlSpin{to{transform:rotate(360deg)}}
.wl-success{background:rgba(34,197,94,.15);border:1px solid rgba(34,197,94,.3);color:#86EFAC;border-radius:980px;padding:15px 26px;font-size:15px}
.wl-error{color:#FCA5A5;font-size:13px;margin:10px 0 0;text-align:center}

.wl-social{display:flex;justify-content:center;gap:22px}
.wl-social a{color:#6B7280;transition:color .2s}
.wl-social a:hover{color:#D1D5DB}

@media(min-width:768px){
  .wl-form{flex-direction:row}
}
@media(max-width:767px){
  .wl-bar{animation:none}
}
`;

export default WaitlistPage;
